import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Download, Loader2, Sheet, Eye, FileText, Camera, Euro, MapPin, Pencil,
  ArrowUpDown, Search, CheckCircle2, AlertCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";

function startOfMonth(d) { const x = new Date(d); x.setDate(1); return x.toISOString().slice(0, 10); }
function endOfMonth(d) { const x = new Date(d); x.setMonth(x.getMonth() + 1, 0); return x.toISOString().slice(0, 10); }

const ESTADO_MONTAJE_COLOR = {
  pendiente: "bg-zinc-700 text-zinc-300",
  semi_montado: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  montado: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  cancelado: "bg-red-500/20 text-red-300 border-red-500/30",
};
const ESTADO_REPORTE_COLOR = {
  pendiente: "bg-zinc-700 text-zinc-300",
  fotos_sin_firma: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  firma_sin_fotos: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  completo: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
};

// Iter 60.8 — Conciliación FD
const CONCILIACION_FD = {
  cobrada:         { label: "✅ Cobrada",       cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  pendiente_cobro: { label: "⏳ Pdte. cobro",   cls: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  vencida:         { label: "⚠ Vencida",        cls: "bg-red-500/20 text-red-300 border-red-500/30" },
  sin_facturar:    { label: "❌ Sin facturar",  cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  anulada:         { label: "🚫 Anulada",       cls: "bg-zinc-700 text-zinc-400" },
};

export default function MontajesTabla() {
  const today = new Date();
  const [desde, setDesde] = useState(startOfMonth(today));
  const [hasta, setHasta] = useState(endOfMonth(today));
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [filterCliente, setFilterCliente] = useState("__all__");
  const [filterMontaje, setFilterMontaje] = useState("__all__");
  const [filterAlbaran, setFilterAlbaran] = useState("__all__");
  const [filterReporte, setFilterReporte] = useState("__all__");
  const [filterFD, setFilterFD] = useState("__all__");
  const [orden, setOrden] = useState({ col: "fecha", dir: "desc" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/montajes-tabla?desde=${desde}&hasta=${hasta}`);
      setData(data);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [desde, hasta]);

  const descargarCSV = async () => {
    try {
      const r = await api.get(`/montajes-tabla?desde=${desde}&hasta=${hasta}&formato=csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `montajes_${desde}_${hasta}.csv`; a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) { toast.error("Error: " + e.message); }
  };

  const clientes = useMemo(() => Array.from(new Set(data.rows.map((r) => r.cliente).filter(Boolean))).sort(), [data]);
  const estadosM = useMemo(() => Array.from(new Set(data.rows.map((r) => r.estado_montaje))).sort(), [data]);
  const estadosA = useMemo(() => Array.from(new Set(data.rows.map((r) => r.estado_albaran))).sort(), [data]);
  const estadosR = useMemo(() => Array.from(new Set(data.rows.map((r) => r.estado_reporte))).sort(), [data]);

  const filtered = useMemo(() => {
    let arr = data.rows.filter((r) => {
      if (filterCliente !== "__all__" && r.cliente !== filterCliente) return false;
      if (filterMontaje !== "__all__" && r.estado_montaje !== filterMontaje) return false;
      if (filterAlbaran !== "__all__" && r.estado_albaran !== filterAlbaran) return false;
      if (filterReporte !== "__all__" && r.estado_reporte !== filterReporte) return false;
      if (filterFD !== "__all__" && r.conciliacion_fd !== filterFD) return false;
      if (search) {
        const t = `${r.peticion_codigo} ${r.cliente} ${r.espacio} ${r.ciudad} ${r.linea_descripcion}`.toLowerCase();
        if (!t.includes(search.toLowerCase())) return false;
      }
      return true;
    });
    arr = [...arr].sort((a, b) => {
      const v1 = a[orden.col] ?? "";
      const v2 = b[orden.col] ?? "";
      if (typeof v1 === "number" && typeof v2 === "number") return orden.dir === "asc" ? v1 - v2 : v2 - v1;
      return orden.dir === "asc" ? String(v1).localeCompare(String(v2)) : String(v2).localeCompare(String(v1));
    });
    return arr;
  }, [data, search, filterCliente, filterMontaje, filterAlbaran, filterReporte, filterFD, orden]);

  const totalImporte = useMemo(() => filtered.reduce((s, r) => s + (r.importe || 0), 0), [filtered]);

  const toggleSort = (col) => {
    if (orden.col === col) setOrden({ col, dir: orden.dir === "asc" ? "desc" : "asc" });
    else setOrden({ col, dir: "asc" });
  };

  const editarImporte = async (row) => {
    const actual = row.importe_montaje ?? row.importe ?? 0;
    const nuevo = prompt(
      `Precio de MONTAJE acordado para "${row.espacio}" (€)\n\n` +
      `Nota: este campo edita solo el precio del montaje. ` +
      `El importe total mostrado incluye montaje + producción/material.`,
      actual,
    );
    if (nuevo === null) return;
    const valor = parseFloat(String(nuevo).replace(",", "."));
    if (isNaN(valor)) { toast.error("Importe inválido"); return; }
    try {
      await api.patch(`/lineas/${row.linea_id}`, { precio_montaje: valor });
      toast.success(`Precio montaje actualizado: ${valor.toFixed(2)} €`);
      load();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const Th = ({ col, children, align = "left" }) => (
    <th className={`text-${align} py-2 px-2 text-[10px] uppercase tracking-wider text-zinc-500 cursor-pointer select-none hover:text-zinc-300`}
      onClick={() => toggleSort(col)} data-testid={`th-${col}`}>
      <span className="inline-flex items-center gap-1">
        {children}
        {orden.col === col && <ArrowUpDown className="w-2.5 h-2.5" />}
      </span>
    </th>
  );

  return (
    <div className="p-4 max-w-[1800px] mx-auto" data-testid="page-montajes-tabla">
      <PageHeader icon={Sheet} title="📋 Mis Montajes (tabla)"
        description="Vista hoja-de-cálculo para cierre mensual: filtra, ordena y exporta a Excel/CSV." />

      {/* Iter 60.8 — Banner conciliación FD */}
      {data.resumen_fd && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="fd-summary">
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300">✅ Cobrado</div>
            <div className="text-lg font-bold text-emerald-200">{data.resumen_fd.cobrado_total.toFixed(2)} €</div>
            <div className="text-[10px] text-emerald-400/70">{data.resumen_fd.n_cobradas} montajes</div>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-blue-300">⏳ Pdte. cobro</div>
            <div className="text-lg font-bold text-blue-200">
              {(data.resumen_fd.pendiente_cobro_total - data.resumen_fd.vencido_total).toFixed(2)} €
            </div>
            <div className="text-[10px] text-blue-400/70">{data.resumen_fd.n_pendientes} montajes</div>
          </div>
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-red-300">⚠ Vencido</div>
            <div className="text-lg font-bold text-red-200">{data.resumen_fd.vencido_total.toFixed(2)} €</div>
            <div className="text-[10px] text-red-400/70">{data.resumen_fd.n_vencidas} montajes</div>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-amber-300">❌ Sin facturar</div>
            <div className="text-lg font-bold text-amber-200">{data.resumen_fd.importe_montaje_sin_facturar.toFixed(2)} €</div>
            <div className="text-[10px] text-amber-400/70">{data.resumen_fd.n_sin_facturar} montajes</div>
          </div>
          <div className="bg-zinc-800/40 border border-zinc-700 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">💰 Facturado total</div>
            <div className="text-lg font-bold text-zinc-200">{data.resumen_fd.facturado_total.toFixed(2)} €</div>
            <div className="text-[10px] text-zinc-500">{data.resumen_fd.n_anuladas > 0 ? `${data.resumen_fd.n_anuladas} anuladas` : "—"}</div>
          </div>
        </div>
      )}

      <Card className="mt-4 bg-zinc-900/60 border-zinc-800">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-2 items-end mb-3">
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Desde</label>
              <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                data-testid="input-desde" className="bg-zinc-950 border-zinc-800 w-36 h-8 text-xs" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Hasta</label>
              <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                data-testid="input-hasta" className="bg-zinc-950 border-zinc-800 w-36 h-8 text-xs" />
            </div>
            <div className="flex-1 max-w-md">
              <label className="text-xs text-zinc-500 block mb-1">Buscar</label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-zinc-600 absolute left-2 top-1/2 -translate-y-1/2" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  data-testid="search" placeholder="Petición, cliente, espacio, ciudad…"
                  className="bg-zinc-950 border-zinc-800 h-8 text-xs pl-7" />
              </div>
            </div>
            <Button onClick={descargarCSV} data-testid="btn-csv"
              className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-semibold h-8 text-xs">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV (Excel)
            </Button>
            <Button onClick={async () => {
              if (!confirm("¿Enviar AHORA recordatorios de cobro a clientes con facturas vencidas?\n\n• L1 (7-14 días): tono cordial\n• L2 (15-30 días): tono firme\n• L3 (31+ días): tono urgente\n\nCada nivel se envía 1 sola vez por factura.")) return;
              try {
                const { data } = await api.post("/admin/notificaciones/recordatorios-cobro/run");
                toast.success(`Recordatorios enviados: ${data.enviados} (omitidos: ${data.omitidos})`);
              } catch (e) { toast.error("Error: " + (e?.response?.data?.detail || e.message)); }
            }} data-testid="btn-recordatorios-cobro"
              className="bg-orange-500 hover:bg-orange-400 text-white h-8 text-xs"
              title="Enviar recordatorios de cobro automáticos (3 niveles según días vencidos)">
              📨 Recordar cobros
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            <Select value={filterCliente} onValueChange={setFilterCliente}>
              <SelectTrigger className="h-8 w-44 bg-zinc-950 border-zinc-800 text-xs" data-testid="f-cliente"><SelectValue placeholder="Cliente" /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="__all__">Todos los clientes</SelectItem>
                {clientes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterMontaje} onValueChange={setFilterMontaje}>
              <SelectTrigger className="h-8 w-40 bg-zinc-950 border-zinc-800 text-xs" data-testid="f-montaje"><SelectValue placeholder="Montaje" /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="__all__">Cualquier montaje</SelectItem>
                {estadosM.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterAlbaran} onValueChange={setFilterAlbaran}>
              <SelectTrigger className="h-8 w-40 bg-zinc-950 border-zinc-800 text-xs" data-testid="f-albaran"><SelectValue placeholder="Albarán" /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="__all__">Cualquier albarán</SelectItem>
                {estadosA.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterReporte} onValueChange={setFilterReporte}>
              <SelectTrigger className="h-8 w-40 bg-zinc-950 border-zinc-800 text-xs" data-testid="f-reporte"><SelectValue placeholder="Reporte" /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="__all__">Cualquier reporte</SelectItem>
                {estadosR.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterFD} onValueChange={setFilterFD}>
              <SelectTrigger className="h-8 w-44 bg-zinc-950 border-zinc-800 text-xs" data-testid="f-fd"><SelectValue placeholder="Estado FD" /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="__all__">Cualquier estado FD</SelectItem>
                {Object.entries(CONCILIACION_FD).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto text-xs text-zinc-400 flex items-center gap-3">
              <span><span className="text-amber-300 font-semibold">{filtered.length}</span> de {data.total} filas</span>
              <span className="text-emerald-300 font-mono">{totalImporte.toFixed(2)} €</span>
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando…</div>
          ) : (
            <div className="overflow-x-auto max-h-[70vh] border border-zinc-800 rounded">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-zinc-900 border-b border-zinc-800">
                  <tr>
                    <Th col="fecha">Fecha</Th>
                    <Th col="peticion_codigo">Petición</Th>
                    <Th col="cliente">Cliente</Th>
                    <Th col="espacio">Espacio</Th>
                    <Th col="ciudad">Ciudad</Th>
                    <Th col="linea_descripcion">Descripción</Th>
                    <Th col="estado_montaje">Montaje</Th>
                    <Th col="estado_albaran">Albarán</Th>
                    <Th col="estado_reporte">Reporte</Th>
                    <Th col="conciliacion_fd">Factura FD</Th>
                    <Th col="importe" align="right">Importe €</Th>
                    <th className="py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={`${r.ruta_id}-${r.linea_id}`}
                      className="border-b border-zinc-900 hover:bg-zinc-900/40"
                      data-testid={`row-${r.linea_id}`}>
                      <td className="py-1.5 px-2 text-zinc-300 whitespace-nowrap">{r.fecha}</td>
                      <td className="py-1.5 px-2 text-amber-300">
                        <Link to={`/peticiones/${r.peticion_id}`} target="_blank" className="hover:underline" data-testid={`link-peticion-${r.linea_id}`}>{r.peticion_codigo}</Link>
                      </td>
                      <td className="py-1.5 px-2 text-zinc-300">{r.cliente || "—"}</td>
                      <td className="py-1.5 px-2 text-zinc-200">{r.espacio}<div className="text-[10px] text-zinc-500">{r.linea_tipo}</div></td>
                      <td className="py-1.5 px-2 text-zinc-400 inline-flex items-center gap-1">
                        <MapPin className="w-2.5 h-2.5" />{r.ciudad}
                      </td>
                      <td className="py-1.5 px-2 text-zinc-300 max-w-[300px] truncate" title={r.linea_descripcion}>{r.linea_descripcion}</td>
                      <td className="py-1.5 px-2">
                        <Badge className={`text-[10px] ${ESTADO_MONTAJE_COLOR[r.estado_montaje] || ESTADO_MONTAJE_COLOR.pendiente}`}>{r.estado_montaje}</Badge>
                      </td>
                      <td className="py-1.5 px-2">
                        {r.albaran_id ? (
                          <Link to={`/albaranes`} target="_blank" data-testid={`link-albaran-${r.linea_id}`}>
                            <Badge className="text-[10px] bg-blue-500/20 text-blue-300 border-blue-500/30">{r.estado_albaran}</Badge>
                          </Link>
                        ) : (
                          <Link to={`/albaranes`} target="_blank" data-testid={`link-crear-albaran-${r.linea_id}`}
                            className="text-[10px] text-red-300 hover:text-red-200 underline">
                            Crear albarán
                          </Link>
                        )}
                      </td>
                      <td className="py-1.5 px-2">
                        {r.estado_reporte === "completo" ? (
                          <Badge className={`text-[10px] ${ESTADO_REPORTE_COLOR.completo}`}>
                            <CheckCircle2 className="w-2.5 h-2.5 mr-1" />completo
                          </Badge>
                        ) : (
                          <Link to={`/peticiones/${r.peticion_id}`} target="_blank" data-testid={`link-reporte-${r.linea_id}`}>
                            <Badge className={`text-[10px] ${ESTADO_REPORTE_COLOR[r.estado_reporte] || ESTADO_REPORTE_COLOR.pendiente}`}>
                              <AlertCircle className="w-2.5 h-2.5 mr-1" />{r.estado_reporte}
                            </Badge>
                          </Link>
                        )}
                      </td>
                      {/* Iter 60.8 — Columna conciliación FD */}
                      <td className="py-1.5 px-2" data-testid={`fd-cell-${r.linea_id}`}>
                        <div className="flex flex-col gap-0.5">
                          <Badge className={`text-[10px] ${(CONCILIACION_FD[r.conciliacion_fd] || CONCILIACION_FD.sin_facturar).cls}`}>
                            {(CONCILIACION_FD[r.conciliacion_fd] || CONCILIACION_FD.sin_facturar).label}
                          </Badge>
                          {r.fd_numero && (
                            <div className="text-[10px] text-zinc-400 font-mono">
                              {r.fd_numero}
                              {r.fd_total > 0 && (
                                <span className={`ml-1 ${r.fd_pending <= 0.01 ? "text-emerald-300" : "text-blue-300"}`}>
                                  {r.fd_total.toFixed(2)}€
                                  {r.fd_pending > 0.01 && r.fd_paid_amount > 0 && (
                                    <span className="text-amber-400 ml-1">({r.fd_pending.toFixed(2)}€ pdte.)</span>
                                  )}
                                </span>
                              )}
                            </div>
                          )}
                          {r.conciliacion_fd === "sin_facturar" && r.estado_montaje === "montado" && (
                            <Link to={`/albaranes`} target="_blank" data-testid={`link-facturar-${r.linea_id}`}
                              className="text-[10px] text-amber-300 hover:text-amber-200 underline">
                              → Facturar
                            </Link>
                          )}
                        </div>
                      </td>
                      <td className="py-1.5 px-2 text-right">
                        <button onClick={() => editarImporte(r)} data-testid={`edit-importe-${r.linea_id}`}
                          title={r.importe_produccion > 0 ? `Montaje ${(r.importe_montaje ?? 0).toFixed(2)}€ + Producción ${(r.importe_produccion ?? 0).toFixed(2)}€` : "Editar precio montaje"}
                          className={`font-mono inline-flex items-center gap-1 hover:bg-zinc-800 rounded px-1.5 py-0.5 ${r.importe ? "text-emerald-300" : "text-zinc-500"}`}>
                          <span className="flex flex-col items-end">
                            <span>{r.importe.toFixed(2)} <Pencil className="w-2.5 h-2.5 opacity-60 inline" /></span>
                            {r.importe_produccion > 0 && (
                              <span className="text-[9px] text-zinc-500 font-normal">
                                M {(r.importe_montaje ?? 0).toFixed(2)} · P {(r.importe_produccion ?? 0).toFixed(2)}
                              </span>
                            )}
                          </span>
                        </button>
                      </td>
                      <td className="py-1.5 px-2">
                        <Link to={`/peticiones/${r.peticion_id}`} target="_blank" title="Ver petición" data-testid={`link-ver-${r.linea_id}`}>
                          <Eye className="w-3.5 h-3.5 text-zinc-500 hover:text-zinc-300" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={12} className="py-8 text-center text-zinc-500">Sin resultados con los filtros aplicados.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
